import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetElectricityActivityList } from '@shared/api/hooks/ccf/activities/electricity';
import Text from '@shared/components/content/text';
import { useCCF } from '../../ccf-context';
import ElectricityEditForm from '../../components/scope-2/electricity-edit-form';
import ElectricityTable from '../../components/scope-2/electricity-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import EmptyCategoryPage from '../ccf-category-intro-page';

export default function ElectricityPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.electricity);
  const {
    showSimpleDialog: showElectricityDialog,
    SimpleDialogComponent: ElectricityDialog,
    closeSimpleDialog: closeElectricityDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, supplier } =
    useDataTableQueryState({
      filters: ['supplier', 'period', 'site'],
    });
  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isFetching,
    status,
  } = useGetElectricityActivityList(reportUuid!, {
    page,
    page_size: perPage,
    suppliers: supplier || undefined,
    time_periods: period || undefined,
    sites: site || undefined,
    sort: formatMultipleSortingForEndpoint(sort! || []),
  });

  const description = (
    <>
      <Text>
        Electricity refers to the{' '}
        <span className="font-semibold">energy your business purchases</span>{' '}
        from external sources to power operations. These purchases lead to
        indirect emissions (Scope 2), which are generated during the production
        of electricity and attributed to your business based on energy
        consumption.
      </Text>
      <Text className="mt-3">Examples include:</Text>
      <Text>
        <ul className="list-disc pl-5">
          <li>
            Power purchased from the{' '}
            <span className="font-semibold">national grid</span> to support
            lighting, heating, and running equipment at your facilities.
          </li>
          <li>
            <span className="font-semibold">
              Renewable energy credits (RECs);
            </span>{' '}
            certificates purchased to offset emissions from grid electricity or
            support clean energy production.
          </li>
        </ul>
      </Text>
    </>
  );

  return (
    <Page name="Scope 2 - Electricity" page="subpage">
      {category.hasActivityData ? (
        <ElectricityTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isFetching={isFetching}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <EmptyCategoryPage
          categorySlug="electricity"
          description={description}
          primaryActionProps={{
            onClick: showElectricityDialog,
          }}
        />
      )}
      {ElectricityDialog({
        header: 'Add electricity data',
        content: <ElectricityEditForm closeDialog={closeElectricityDialog} />,
      })}
    </Page>
  );
}
